const { useCallback, useEffect, useState } = require('react');
const { replace } = require('connected-react-router');
const DiagnosticsPanel = require('components/DiagnosticsPanel');
const Trace = require('utils/app-diagnostics');
const Firebase = require('utils/firebasex');
const { getPushPresentation } = require('utils/push-diagnostics');
const Continuation = require('services/auth-management/continuation');

module.exports = AppDiagnostics;

const internals = {};

function AppDiagnostics({ store, history }) {

    const platformEnabled = typeof window !== 'undefined'
        && (window.cordova?.platformId === 'ios' || window.device?.platform?.toLowerCase() === 'ios');
    const [enabled, setEnabled] = useState(Trace.isEnabled);
    const [view, setView] = useState(null);
    const refresh = useCallback(() => {

        if (!Trace.isEnabled()) { return; }
        const { context } = require('app-context');
        const trace = Trace.getSnapshot();
        const continuation = Continuation.peek();
        const next = {
            trace,
            push: Trace.getPushSummary(trace, Firebase.getDiagnostics()),
            app: Trace.getAppState({ selectors: context.selectors.all, state: store.getState(), location: history.location }),
            continuation: { redirect: continuation.redirect, target: continuation.target ? Trace.safeLocation(continuation.target) : undefined }
        };
        setView((previous) => JSON.stringify(previous) === JSON.stringify(next) ? previous : next);
    }, [store, history]);

    useEffect(() => {

        if (!platformEnabled) { return; }
        const update = () => {

            const { context } = require('app-context');
            const debug = context.selectors.all.getDebug?.(store.getState());
            if (debug) { Trace.setEnabled(debug.appDiagnostics === true); }
            else { Trace.setEnabled(Trace.isEnabled()); }
            setEnabled(Trace.isEnabled());
            if (Trace.isEnabled()) { refresh(); }
            else { setView(null); }
        };
        update();
        const unsubscribe = store.subscribe(update);
        const onToggle = () => {

            setEnabled(Trace.isEnabled());
            if (Trace.isEnabled()) { refresh(); }
            else { setView(null); }
        };
        window.addEventListener('nearpeer:app-diagnostics-enabled', onToggle);
        return () => {

            unsubscribe();
            window.removeEventListener('nearpeer:app-diagnostics-enabled', onToggle);
        };
    }, [platformEnabled, store, refresh]);

    useEffect(() => {

        if (!platformEnabled || !enabled) { return; }
        refresh();
        const unlisten = history.listen((update) => {

            Trace.record('navigation.committed', { route: Trace.safeLocation(update.location || update) });
            refresh();
        });
        window.addEventListener('nearpeer:app-diagnostics', refresh);
        return () => {

            unlisten();
            window.removeEventListener('nearpeer:app-diagnostics', refresh);
        };
    }, [platformEnabled, enabled, history, refresh]);

    if (!platformEnabled || !enabled || !view) {
        return null;
    }

    const refreshNative = async () => {

        if (!Trace.isEnabled()) { return; }
        const recordingSession = Trace.getSnapshot().recordingSession;

        const bridge = window.NearpeerAppLinks;
        if (bridge?.getDiagnostics) {
            const snapshot = await internals.getNativeSnapshot(bridge);
            if (!Trace.isEnabled() || Trace.getSnapshot().recordingSession !== recordingSession) { return; }
            window.__nearpeerAppDiagnostics.native = snapshot;
            window.__nearpeerAppDiagnostics.nativeRefreshedAt = new Date().toISOString();
            Trace.record('native.snapshot-refreshed');
        }

        refresh();
    };

    const sections = [
        { id: 'push', title: 'Push technical details', data: view.push },
        { id: 'native', title: 'Native app and bridge', data: Object.keys(view.trace.native || {}).length
            ? view.trace.native
            : { status: 'No native diagnostics reported. Check that the rebuilt app contains the updated local plugin.' } },
        { id: 'frontend', title: 'Frontend build', data: { appVersion: process.env.APP_VERSION, diagnosticsRevision: 'push-delivery-v3', bundle: view.trace.frontendBundle || 'not reported' } },
        { id: 'app', title: 'Current app state', data: view.app },
        { id: 'continuation', title: 'Saved destination', data: view.continuation }
    ];
    const actions = [
        { id: 'navigate', label: 'Test router only', onClick: () => {

            Trace.record('test.router', { destination: Trace.safeLocation('/app/classes') });
            store.dispatch(replace('/app/classes'));
        } },
        { id: 'clear', label: 'Start new test', onClick: Trace.clear }
    ];
    const { summary, checks, events } = getPushPresentation({ trace: view.trace, receiver: view.push.receiver, app: view.app });
    const instructions = [
        'Tap Start new test to clear earlier events.',
        'Close Diagnostics, put the app in the background, then tap the push notification.',
        'Reopen Diagnostics. The result and checks show where handling stopped; the latest events appear first.',
        'Test router only opens Groups directly. It does not test push notification delivery.'
    ];

    return <DiagnosticsPanel title='Diagnostics' summary={summary} checks={checks} instructions={instructions}
        sections={sections} events={events} actions={actions} onRefresh={refreshNative} autoRefreshOnOpen />;
}

internals.getNativeSnapshot = (bridge) => new Promise((resolve, reject) => {

    const timer = setTimeout(() => reject(new Error('Native diagnostics did not respond within 5 seconds.')), 5000);
    const finish = (callback) => (value) => {

        clearTimeout(timer);
        callback(value);
    };

    try {
        bridge.getDiagnostics(finish(resolve), finish(() => reject(new Error('Native diagnostics request failed.'))));
    }
    catch (_) {
        clearTimeout(timer);
        reject(new Error('Native diagnostics request failed.'));
    }
});
